// ─────────────────────────────────────────────────────────────────────────────
// Generate the feeder — the code the user's computer runs to feed the widget.
//
// One route, GET /feeds, returning plain integers per declared feed:
//
//   { feeds: { "0xB241": { value: 72, auxiliary: 1 } } }
//
// No bit packing, no protocol, no begin/commit — the Designer (or a host app)
// relays these numbers to the widget as host.rpc packets. The generated file
// carries the user's own friendly names and field labels as comments, so it
// reads as THEIR data source, not as firmware plumbing.
//
// UI layer on purpose: src/compiler is frozen, and this file's architecture
// (dependency-free node:http, CORS, --port flag) deliberately mirrors the
// proven generateHostServer shape in compiler/hostServer.ts.
// ─────────────────────────────────────────────────────────────────────────────

export interface FeedServerFeed {
  /** Display id — "0xB241". */
  hex: string;
  /** Friendly name ("Current conditions"); falls back to the id. */
  name: string;
  /** Plain-language labels for the two integer payload fields. */
  valueLabel: string;
  auxLabel: string;
  /** Seed values — the last numbers tested in the Designer. */
  value: number;
  auxiliary: number;
}

export function feedServerSlug(widgetName: string): string {
  return (
    widgetName.toLowerCase().replace(/[^a-z0-9]+/gu, "-").replace(/^-+|-+$/gu, "") || "widget"
  );
}

export const FEED_SERVER_PORT = 842;

/** Default endpoint the Designer suggests for the feeder. */
export function feedServerEndpoint(port = FEED_SERVER_PORT): string {
  return `http://localhost:${port}/feeds`;
}

export function generateFeedServer(
  widgetName: string,
  feeds: FeedServerFeed[],
  port = FEED_SERVER_PORT,
): string {
  const slug = feedServerSlug(widgetName);
  const entries = feeds
    .map((f) => {
      const label = f.name && f.name !== f.hex ? `${f.name} — ` : "";
      return [
        `      // ${label}value: ${f.valueLabel} · auxiliary: ${f.auxLabel}`,
        `      "${f.hex}": { value: ${f.value}, auxiliary: ${f.auxiliary} },`,
      ].join("\n");
    })
    .join("\n");

  return `#!/usr/bin/env node
// Feeder for "${widgetName}" — generated by the F1 Widget Designer.
//
// THIS IS THE CODE YOUR COMPUTER RUNS TO FEED THE WIDGET. It returns plain
// integers; the Designer relays them into the running widget as host.rpc
// packets — the same numbers a host app sends the keyboard. No bits, no
// bytes, no protocol knowledge lives in this file.
//
//   node ${slug}-feeder.mjs            # http://localhost:${port}
//   node ${slug}-feeder.mjs --port 9000
//
// Then in the Designer: Source → Host data → "Fetch and send".

import { createServer } from "node:http";

const args = process.argv.slice(2);
const portFlag = args.indexOf("--port");
const PORT = portFlag >= 0 ? Number(args[portFlag + 1]) : ${port};

function readFeeds() {
  // Replace these numbers with your real values — read a sensor, call an
  // API, whatever. Each key is one feed your widget script handles.
  return {
    feeds: {
${entries}
    },
    note: "${slug} feeder " + new Date().toLocaleTimeString(),
  };
}

const server = createServer((request, response) => {
  const url = new URL(request.url, \`http://localhost:\${PORT}\`);
  // The Designer runs on a different origin, so CORS is required.
  response.setHeader("access-control-allow-origin", "*");
  response.setHeader("access-control-allow-headers", "content-type");
  if (request.method === "OPTIONS") { response.statusCode = 204; response.end(); return; }

  if (url.pathname !== "/feeds") {
    response.statusCode = 404;
    response.setHeader("content-type", "application/json");
    response.end(JSON.stringify({ error: \`No route \${url.pathname}. Try /feeds.\` }));
    return;
  }
  try {
    const body = JSON.stringify(readFeeds());
    response.setHeader("content-type", "application/json");
    response.end(body);
    console.log(\`\${request.method} \${url.pathname} -> \${body.length} bytes\`);
  } catch (cause) {
    response.statusCode = 500;
    response.end(JSON.stringify({ error: String(cause?.message ?? cause) }));
  }
});

server.listen(PORT, () => {
  console.log(\`${widgetName} feeder on http://localhost:\${PORT}/feeds\`);
});
`;
}
